import { useEffect, useId, useRef, useState } from 'react'
import {
  currentMonthRange,
  formatRangeLabel,
  lastDaysRange,
  monthRange,
  type ReportRange,
  sameRange,
} from '@/entities/report'
import { cn, zoneDayHint, zoneLabel } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Field, Input } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { useAnchoredPopover } from '@/shared/ui/popover'

interface PeriodPickerProps {
  range: ReportRange
  onChange: (next: ReportRange) => void
}

// Период общий для всех срезов и живёт в URL: ссылкой на отчёт за конкретный
// интервал делятся. Пресеты закрывают повседневное, произвольный интервал
// набирается датами; перевёрнутый ввод разворачивается сам (normalizeRange).
export function PeriodPicker({ range, onChange }: PeriodPickerProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const {
    anchorRef: triggerRef,
    popoverRef: panelRef,
    popoverProps,
  } = useAnchoredPopover<HTMLButtonElement, HTMLDivElement>({ open, align: 'end' })
  const panelId = useId()
  // Границы пресетов считаются один раз: «прошлый месяц» не должен разъезжаться
  // между открытиями панели.
  const [presets] = useState(() => [
    { label: 'Текущий месяц', range: currentMonthRange() },
    { label: 'Прошлый месяц', range: monthRange(-1) },
    { label: '30 дней', range: lastDaysRange(30) },
    { label: '90 дней', range: lastDaysRange(90) },
  ])
  const defaultRange = presets[0]?.range ?? range

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (
        !rootRef.current?.contains(e.target as Node) &&
        !panelRef.current?.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, panelRef, triggerRef])

  return (
    <div ref={rootRef} className="inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Период отчёта: ${formatRangeLabel(range)}`}
        title={zoneDayHint()}
        {...(open ? { 'aria-controls': panelId } : {})}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex h-9 items-center gap-2 rounded-control border px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
          open
            ? 'border-border-accent bg-bg-elevated text-text-primary'
            : 'border-border-default text-text-secondary hover:border-border-strong hover:text-text-primary',
        )}
      >
        <Icon name="calendar" size={16} />
        {formatRangeLabel(range)}
      </button>
      {open && (
        <div
          ref={panelRef}
          {...popoverProps}
          role="dialog"
          aria-label="Период отчёта"
          id={panelId}
          className="m-0 w-[min(20rem,calc(100vw-2rem))] rounded-card border border-border-default bg-bg-surface p-4 shadow-lg shadow-black/40"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  aria-pressed={sameRange(range, preset.range)}
                  onClick={() => {
                    onChange(preset.range)
                    close()
                  }}
                  className={cn(
                    'rounded-control border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
                    sameRange(range, preset.range)
                      ? 'border-border-accent bg-bg-elevated text-text-primary'
                      : 'border-border-default text-text-secondary hover:border-border-strong hover:text-text-primary',
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <Field label="Начало периода">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={range.from}
                  onChange={(e) => e.target.value && onChange({ ...range, from: e.target.value })}
                />
              )}
            </Field>
            <Field label="Конец периода">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={range.to}
                  onChange={(e) => e.target.value && onChange({ ...range, to: e.target.value })}
                />
              )}
            </Field>
            {/* Границы периода — календарные дни зоны организации: тот же
                календарь, что у бэкенда, и сутки не съезжают на офсет. */}
            <p className="label text-text-tertiary" title={zoneDayHint()}>
              Сутки периода — по зоне {zoneLabel()}
            </p>
            <div className="flex items-center justify-between gap-3 border-t border-border-subtle pt-3">
              <button
                type="button"
                disabled={sameRange(range, defaultRange)}
                onClick={() => {
                  onChange(defaultRange)
                  close()
                }}
                className="text-sm text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:opacity-40 disabled:hover:text-text-secondary"
              >
                Текущий месяц
              </button>
              <Button size="sm" variant="secondary" onClick={close}>
                Готово
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
